import {Component, OnInit} from '@angular/core';
import {DisplayedProduct, ModifierGroupInfo} from "app/models/menu.model";
import {ModifierGroupApiService} from 'app/services/menu-edit/modifier-group-api.service';
import {ActivatedRoute} from "@angular/router";
import {firstValueFrom, Observable} from "rxjs";
import {Select} from "@ngxs/store";
import {OrganizationsState} from "app/state-management/states/organizations.states";
import {Organization} from "app/models/organization.model";
import {
  ModifierGroupModalComponent, ModifierGroupModalData
} from "app/pages/menu-edit/modifier-group/modifier-group-modal/modifier-group-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {UntilDestroy} from "@ngneat/until-destroy";
import {
  OnCheckedModifierGroup
} from "app/pages/menu-edit/modifier-group/modifier-group-item/modifier-group-item.component";

@UntilDestroy()
@Component({
  selector: 'app-modifier-group',
  templateUrl: './modifier-group.component.html',
  styleUrls: ['./modifier-group.component.scss']
})
export class ModifierGroupComponent implements OnInit {
  selectedModifierGroupIds: Set<string> = new Set();
  modifierGroups: ModifierGroupInfo[] = [];
  organization: Organization;
  modifiers: Map<string, DisplayedProduct> = new Map();
  loading = false;
  error: string = null;
  @Select(OrganizationsState.getSelectedOrganization)
  selectedOrganization$: Observable<Organization>;

  constructor(
    public activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    private modifierGroupService: ModifierGroupApiService,
  ) {
  }

  ngOnInit(): void {
    this.getOrganizationAndData().then(_ => this.loading = false);
  }

  async getOrganizationAndData(): Promise<void> {
    try {
      this.loading = true;
      this.organization = await firstValueFrom(this.selectedOrganization$);

      const modifiers = await this.modifierGroupService.getModifierListAsync(this.organization.id);
      this.modifiers = modifiers.reduce((accumulator: Map<string, DisplayedProduct>, m: DisplayedProduct) => {
          accumulator.set(m.externalId, m);
          return accumulator;
        },
        new Map<string, DisplayedProduct>());

      this.modifierGroups = await this.modifierGroupService.getModifierGroupsAsync(this.organization.id);
    } catch (error) {
      console.error('Failed to initialize:', error);
    } finally {
      this.loading = false;
    }
  }

  onClickCreateModifierGroup(): void {
    this.dialog.open(
      ModifierGroupModalComponent, {
        data: {
          modifierGroup: {
            id: '',
            name: '',
            description: '',
            nameTranslations: {},
            descriptionTranslations: {},
            modifierProducts: [],
          } as ModifierGroupInfo,
          organization: this.organization,
          modifiers: this.modifiers,
        } as ModifierGroupModalData
      }
    ).afterClosed().subscribe(async (res) => {
      if (!res) {
        return;
      }
      this.modifierGroups.push(res);
      await this.getOrganizationAndData();
    });
  }

  selectModifierGroupHandler(checkedInfo: OnCheckedModifierGroup): void {
    if (checkedInfo.checked && !this.selectedModifierGroupIds.has(checkedInfo.modifierGroupId)) {
      this.selectedModifierGroupIds.add(checkedInfo.modifierGroupId);
    }

    if (!checkedInfo.checked && this.selectedModifierGroupIds.has(checkedInfo.modifierGroupId)) {
      this.selectedModifierGroupIds.delete(checkedInfo.modifierGroupId);
    }
  }

  onLoadingHandler(arg: boolean): void {
    this.loading = arg;
  }

  async onDeleteHandler(modifierGroupId: string): Promise<void> {
    this.modifierGroups = [...this.modifierGroups.filter(mg => mg.id !== modifierGroupId)];
    await this.getOrganizationAndData();
  }

  async onApplyModifierGroup(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const result = await this.modifierGroupService.applyModifierGroupsAsync(
        this.organization.id,
        [...this.selectedModifierGroupIds.values()]
      );

      if (result === 'good') {
        await this.getOrganizationAndData();
      } else {
        this.error = result || 'Error occurred';
      }
    } catch (error) {
      console.error('Failed to apply modifier groups:', error);
      this.error = 'An error occurred';
    } finally {
      this.loading = false;
    }
  }
}
